import React, { useMemo } from "react";

import { mdiCircle } from "@mdi/js";
import Icon, { Stack } from "@mdi/react";

import Modal from "./Modal";

import type { JSX } from "react";

import type { ModalProps } from "./Modal";

export const MODAL_VARIANTS = {
  DELETE: "delete",
  PRIMARY: "primary",
  SECONDARY: "secondary",
} as const;

type ModalVariantKeys = keyof typeof MODAL_VARIANTS;

export type ModalVariants = (typeof MODAL_VARIANTS)[ModalVariantKeys];

interface ConfirmModalProps extends Omit<ModalProps, "header"> {
  iconPath?: string;
  title?: string;
  variant?: ModalVariants;
}

interface BackgroundColorIcon {
  first: string;
  secondary: string;
}

function ConfirmModal({
  isOpen,
  onClose,
  children,
  actions,
  iconPath,
  size,
  title,
  withDarkBackground,
  variant = MODAL_VARIANTS.DELETE,
}: ConfirmModalProps): JSX.Element | null {
  const backgroundColorIcon: BackgroundColorIcon = useMemo(() => {
    switch (variant) {
      case MODAL_VARIANTS.PRIMARY:
        return {
          first: "color-mix(in srgb, var(--primary) 10%, transparent)",
          secondary: "color-mix(in srgb, var(--primary) 20%, transparent)",
        };
      case MODAL_VARIANTS.DELETE:
      default:
        return {
          first: "color-mix(in srgb, var(--destructive) 10%, transparent)",
          secondary: "color-mix(in srgb, var(--destructive) 20%, transparent)",
        };
    }
  }, [variant]);

  const modalIcon = iconPath ? (
    <Stack size={2.34375}>
      <Icon color={backgroundColorIcon.first} path={mdiCircle} size={2.65625} />
      <Icon
        color={backgroundColorIcon.secondary}
        path={mdiCircle}
        size={1.875}
      />
      <Icon path={iconPath} size={0.9375} />
    </Stack>
  ) : null;

  return (
    <Modal
      actions={actions}
      header={modalIcon}
      isOpen={isOpen}
      onClose={onClose}
      size={size}
      withDarkBackground={withDarkBackground}
    >
      {title ? (
        <h3 className="bg-card text-foreground sticky top-0 overflow-hidden pb-[0.3125rem] text-[0.9375rem] font-medium text-ellipsis">
          {title}
        </h3>
      ) : null}
      <div className="break-words">{children}</div>
    </Modal>
  );
}

export default ConfirmModal;
